import { useEffect, useState } from "react";
import { X, Loader2, Clock, Gauge, BookOpen, CheckCircle2, MapPin, Phone, CalendarDays, Star, Trophy, Users } from "lucide-react";
import { api, apiError } from "@/lib/api";

const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const STATUS = {
  APPROVED: ["Validée", "bg-emerald-50 text-emerald-700"],
  PENDING: ["En attente de validation", "bg-amber-50 text-amber-700"],
  REJECTED: ["Refusée", "bg-red-50 text-red-700"],
  DRAFT: ["Brouillon", "bg-neutral-100 text-neutral-600"],
};
const euro = (v) => (v == null ? "—" : `${Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} €`);
const gearboxLabel = (g) => (!g ? "—" : /auto/i.test(g) ? "Automatique" : /manu/i.test(g) ? "Manuelle" : g);
const dateFr = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "—");

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" />
      <span className="w-28 shrink-0 text-neutral-500">{label}</span>
      <span className="min-w-0 break-words font-semibold text-neutral-900">{children}</span>
    </div>
  );
}

// Vue admin, lecture seule : tous les forfaits d'une auto-école, en entier.
export function SchoolOffersModal({ school, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!school) return undefined;
    setData(null);
    setError("");
    api.get(`/admin/schools/${school.id}/offers`)
      .then(({ data: d }) => setData(d))
      .catch((e) => setError(apiError(e.response?.data?.detail) || e.message));
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [school, onClose]);

  if (!school) return null;
  // Les plus réservés du mois en premier (ordre d'origine conservé à égalité)
  const offers = [...(data?.offers || [])].sort((a, b) => (b.reserved_this_month || 0) - (a.reserved_this_month || 0));
  const cm = data?.current_month;
  const monthLabel = cm ? `${MONTHS[cm.month - 1]} ${cm.year}` : "";
  const months = data?.months || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-6" onClick={onClose} data-testid="school-offers-modal">
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 border-b border-neutral-200 px-5 py-4">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-400">Forfaits — lecture seule</p>
            <h2 className="mt-0.5 truncate text-xl font-extrabold tracking-tight" data-testid="school-offers-title">{school.name}</h2>
            {data?.school && (
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500">
                {data.school.address && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {data.school.address}</span>}
                {data.school.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {data.school.phone}</span>}
              </div>
            )}
          </div>
          <button onClick={onClose} aria-label="Fermer" data-testid="school-offers-close" className="shrink-0 rounded-lg p-1.5 text-neutral-500 hover:bg-neutral-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          {error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : !data ? (
            <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
          ) : (
            <>
              <section data-testid="school-offers-places">
                <h3 className="flex items-center gap-2 text-sm font-bold text-neutral-900"><CalendarDays className="h-4 w-4 text-brand" /> Places déclarées (12 prochains mois)</h3>
                {months.length ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {months.map((m) => (
                      <span key={`${m.year}-${m.month}`} className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-xs" data-testid={`school-offers-month-${m.year}-${m.month}`}>
                        <strong className="capitalize">{MONTHS[m.month - 1]} {m.year}</strong> : {m.places} place(s) · {m.reserved} réservée(s) · <span className={m.remaining > 0 ? "font-bold text-emerald-700" : "font-bold text-red-600"}>{m.remaining} restante(s)</span>
                      </span>
                    ))}
                  </div>
                ) : <p className="mt-2 text-sm text-neutral-400">Aucune place déclarée.</p>}
              </section>

              <div className="mt-6 flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-bold text-neutral-900">{offers.length} forfait(s)</h3>
                {cm && (
                  <p className="text-xs text-neutral-500" data-testid="school-offers-month-total">
                    <strong className="text-neutral-900">{cm.reserved}</strong> réservation(s) en {monthLabel}, tous forfaits confondus
                  </p>
                )}
              </div>
              {offers.length === 0 && <p className="mt-2 text-sm text-neutral-400">{"Cette auto-école n'a publié aucun forfait."}</p>}
              <div className="mt-3 space-y-4">
                {offers.map((o) => {
                  const [stLabel, stCls] = STATUS[o.status] || [o.status || "—", "bg-neutral-100 text-neutral-600"];
                  return (
                    <article key={o.id} className={`rounded-xl border p-4 ${o.most_reserved ? "border-amber-300 bg-amber-50/40" : "border-neutral-200"}`} data-testid={`school-offer-${o.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h4 className="break-words text-base font-extrabold text-neutral-900" data-testid={`school-offer-title-${o.id}`}>
                            {o.featured && <Star className="mr-1 inline h-4 w-4 fill-amber-400 text-amber-400" />}{o.title}
                          </h4>
                          <p className="mt-0.5 text-2xl font-extrabold tracking-tight text-brand" data-testid={`school-offer-price-${o.id}`}>{euro(o.price)}</p>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {o.most_reserved && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-400 px-2.5 py-0.5 text-xs font-bold text-amber-950" data-testid={`school-offer-top-${o.id}`}>
                              <Trophy className="h-3.5 w-3.5" /> Le plus réservé
                            </span>
                          )}
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${o.online ? "bg-emerald-600 text-white" : "bg-neutral-200 text-neutral-600"}`} data-testid={`school-offer-online-${o.id}`}>
                            {o.online ? "En ligne" : "Hors ligne"}
                          </span>
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${stCls}`}>{stLabel}</span>
                          {o.suspended && <span className="rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700">Suspendue</span>}
                          {o.active === false && <span className="rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-semibold text-neutral-600">Désactivée</span>}
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm ring-1 ring-neutral-200" data-testid={`school-offer-reserved-${o.id}`}>
                        <Users className="h-4 w-4 shrink-0 text-brand" />
                        <span>
                          <strong className="text-base text-neutral-900">{o.reserved_this_month || 0}</strong> élève(s) ont réservé ce forfait en {monthLabel}
                          {(o.enrolled_this_month || 0) > 0 && <span className="text-emerald-700"> · dont {o.enrolled_this_month} inscrit(s)</span>}
                        </span>
                      </div>
                      <div className="mt-3 grid gap-2 sm:grid-cols-2">
                        <Row icon={Clock} label="Heures">{o.hours ? `${o.hours} h de conduite` : "—"}</Row>
                        <Row icon={Gauge} label="Boîte">{gearboxLabel(o.gearbox)}</Row>
                        <Row icon={BookOpen} label="Code">{o.code_included == null ? "—" : o.code_included ? "Inclus" : "Non inclus"}</Row>
                        <Row icon={CheckCircle2} label="Permis">{o.license_type || "—"}</Row>
                        {(o.city || o.postal_code) && <Row icon={MapPin} label="Lieu">{[o.postal_code, o.city].filter(Boolean).join(" ")}</Row>}
                        <Row icon={CalendarDays} label="Mise à jour">{dateFr(o.updated_at || o.created_at)}</Row>
                      </div>
                      <div className="mt-3">
                        <p className="text-xs font-bold uppercase tracking-wider text-neutral-400">Contenu du forfait</p>
                        {o.options.length ? (
                          <ul className="mt-1.5 grid gap-1 sm:grid-cols-2" data-testid={`school-offer-options-${o.id}`}>
                            {o.options.map((opt, i) => (
                              <li key={i} className="flex items-start gap-1.5 text-sm text-neutral-700"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /><span className="break-words">{opt}</span></li>
                            ))}
                          </ul>
                        ) : <p className="mt-1 text-sm text-neutral-400">Aucune option renseignée.</p>}
                      </div>
                      {o.description && (
                        <div className="mt-3">
                          <p className="text-xs font-bold uppercase tracking-wider text-neutral-400">Description</p>
                          <p className="mt-1 whitespace-pre-line break-words text-sm text-neutral-700">{o.description}</p>
                        </div>
                      )}
                      {(o.review_note || o.suspend_reason) && (
                        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                          {o.suspend_reason ? `Motif de suspension : ${o.suspend_reason}` : `Note de validation : ${o.review_note}`}
                        </p>
                      )}
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
